import { persistentAtom, persistentMap } from '@nanostores/persistent';
import { atom, computed } from 'nanostores';

import { PLAYER_FEATURES } from '../../constants.js';
import type { PlayerFeature } from '../../types.js';
import { booleanEncoder } from './persistent_encoding.js';

const defaultToolbarItems: Record<PlayerFeature, boolean> = {
  audio_mixer: true,
  playbackrate: true,
  subtitles: true,
  compare: false,
  point_in: false,
  point_out: false,
  undo: false,
  redo: false,
  show_actual_size: false,
  fullscreen: true,
  volume: true,
  player_background_color: false,
  flip: false,
  flop: false,
  rotate_counter_clockwise: false,
  rotate_clockwise: false,
  audio_meter: false,
  tracing_layer: false,
  print: false,
  search: false,
  text_select: false,
  tool_select: false,
  tool_zoom: false,
  tool_pan: false,
  tool_pencil: false,
  tool_arrow: false,
  tool_ellipse: false,
  tool_rectangle: false,
  tool_line: false,
  tool_cross: false,
  tool_text: false,
  tool_laser: false,
};

export function createSettingsStore() {
  const $pinnedToolbarItems = persistentMap<Record<PlayerFeature, boolean>>(
    'UP_pinnedToolbarItems',
    defaultToolbarItems,
    booleanEncoder,
  );

  function togglePinnedToolbarItem(feature: PlayerFeature) {
    $pinnedToolbarItems.setKey(feature, !$pinnedToolbarItems.get()[feature]);
  }

  const $allToolbarItemsUnpinned = computed(
    [$pinnedToolbarItems],
    (pinnedToolbarItems) => {
      return Object.entries(pinnedToolbarItems).every(
        ([key, value]) => key && !value,
      );
    },
  );

  function unpinAllToolbarItems() {
    for (const feature of PLAYER_FEATURES) {
      $pinnedToolbarItems.setKey(feature, false);
    }
  }

  function pinDefaultTools() {
    $pinnedToolbarItems.set(defaultToolbarItems);
  }

  function pinToolbarItem(feature: PlayerFeature) {
    $pinnedToolbarItems.setKey(feature, true);
  }
  function unpinToolbarItem(feature: PlayerFeature) {
    $pinnedToolbarItems.setKey(feature, false);
  }

  const $playerBackgroundColor = persistentAtom<string>(
    'UP_playerBackgroundColor',
    '#000000',
  );

  function setPlayerBackgroundColor(color: string) {
    $playerBackgroundColor.set(color);
  }

  const $isPrintAllowed = atom<boolean>(false);

  function setIsPrintAllowed(allow: boolean) {
    $isPrintAllowed.set(allow);
  }

  const $allowClickableLinks = atom<boolean>(false);

  function setAllowClickableLinks(allow: boolean) {
    $allowClickableLinks.set(allow);
  }

  return {
    $isPrintAllowed,
    $allowClickableLinks,
    $pinnedToolbarItems,
    $allToolbarItemsUnpinned,
    $playerBackgroundColor,
    togglePinnedToolbarItem,
    unpinAllToolbarItems,
    pinDefaultTools,
    pinToolbarItem,
    unpinToolbarItem,
    setIsPrintAllowed,
    setAllowClickableLinks,
    setPlayerBackgroundColor,
  };
}
